import { BufferAttribute } from 'three'
import { BufferGeometry } from 'three/src/core/BufferGeometry'

import { Vec2, Vec3, Vec4 } from './helpers'

/** How many times to subdivide planes in the x and y directions each */
const planeSegments = 4

/** How many times to subdivide cylinders' circle geometries */
const cylinderUSegments = 64

const bendTubeSegments = 64

/** How many times to subdivide cylinders' shell geometries */
const cylinderVSegments = 4

/** The number of angled segments to divide cones into */
const angleSegments = 180

/** The number of radial segments to divide cones into */
const radialSegments = 16

/**
 * Creates a BufferGeometry object with the provided buffer attribute arrays
 * @param attributes A map from attribute names to arrays of vectors
 */
const createBufferGeometry = (
  attributes: Record<string, (Vec2 | Vec3 | Vec4)[]>
) => {
  const result = new BufferGeometry()
  Object.entries(attributes).forEach(([key, value]) => {
    result.setAttribute(
      key,
      new BufferAttribute(new Float32Array(value.flat()), value[0].length)
    )
  })
  return result
}

//------------------------------------------------------------------------------

const createPlaneAttributes = () => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()
  const uv = new Array<Vec2>()

  for (let x = 0; x < planeSegments; x++) {
    for (let y = 0; y < planeSegments; y++) {
      const u0 = x / planeSegments
      const v0 = y / planeSegments
      const u1 = (x + 1) / planeSegments
      const v1 = (y + 1) / planeSegments

      position.push([u0, v0, 0.0], [u1, v0, 0.0], [u1, v1, 0.0])
      position.push([u1, v1, 0.0], [u0, v1, 0.0], [u0, v0, 0.0])

      normal.push([0.0, 0.0, 1.0], [0.0, 0.0, 1.0], [0.0, 0.0, 1.0])
      normal.push([0.0, 0.0, 1.0], [0.0, 0.0, 1.0], [0.0, 0.0, 1.0])

      uv.push([u0, v0], [u1, v0], [u1, v1])
      uv.push([u1, v1], [u0, v1], [u0, v0])
    }
  }

  return { position, normal, uv }
}

const createShellAttributes = () => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()
  const uv = new Array<Vec2>()

  for (let i = 0; i < cylinderUSegments; i++) {
    for (let j = 0; j < cylinderVSegments; j++) {
      const uA = i / cylinderUSegments
      const uB = (i + 1) / cylinderUSegments

      const angleA = 2.0 * Math.PI * uA
      const angleB = 2.0 * Math.PI * uB

      const aX = -Math.cos(angleA)
      const aZ = Math.sin(angleA)
      const bX = -Math.cos(angleB)
      const bZ = Math.sin(angleB)
      const y0 = j / cylinderVSegments
      const y1 = (j + 1) / cylinderVSegments

      position.push([aX, y0, aZ], [bX, y0, bZ], [bX, y1, bZ])
      position.push([bX, y1, bZ], [aX, y1, aZ], [aX, y0, aZ])

      normal.push([aX, 0, aZ], [bX, 0, bZ], [bX, 0, bZ])
      normal.push([bX, 0, bZ], [aX, 0, aZ], [aX, 0, aZ])

      uv.push([uA, y0], [uB, y0], [uB, y1])
      uv.push([uB, y1], [uA, y1], [uA, y0])
    }
  }

  return { position, normal, uv }
}

const createCircleAttributes = () => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()
  const uv = new Array<Vec2>()
  const uv2 = new Array<Vec2>()

  for (let i = 0; i < cylinderUSegments; i++) {
    const uA = i / cylinderUSegments
    const uB = (i + 1) / cylinderUSegments
    const angleA = 2.0 * Math.PI * uA
    const angleB = 2.0 * Math.PI * uB

    const aX = Math.cos(angleA)
    const aY = Math.sin(angleA)
    const bX = Math.cos(angleB)
    const bY = Math.sin(angleB)
    position.push([0.0, 0.0, 0.0], [aX / 2, aY / 2, 0.0], [bX / 2, bY / 2, 0])
    normal.push([0.0, 0.0, 1.0], [0.0, 0.0, 1.0], [0.0, 0.0, 1.0])
    const aU = (aX + 1.0) / 2.0
    const aV = (aY + 1.0) / 2.0
    const bU = (bX + 1.0) / 2.0
    const bV = (bY + 1.0) / 2.0
    uv.push([0.5, 0.5], [aU, aV], [bU, bV])
    uv2.push([uA, 0.0], [uA, 1.0], [uB, 1.0])
  }

  return { position, normal, uv, uv2 }
}

const createConeAttributes = () => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()
  const uv = new Array<Vec2>()
  const uv2 = new Array<Vec2>()
  for (let j = 0; j < radialSegments; j++) {
    for (let i = 0; i < angleSegments; i++) {
      const r1 = j / radialSegments
      const r2 = (j + 1) / radialSegments
      const uA = i / angleSegments
      const uB = (i + 1) / angleSegments
      const angleA = 2.0 * Math.PI * uA
      const angleB = 2.0 * Math.PI * uB

      const aX = -Math.sin(angleA)
      const aY = Math.cos(angleA)
      const bX = -Math.sin(angleB)
      const bY = Math.cos(angleB)
      if (j === 0) {
        position.push(
          [0.0, 0.0, 0.0],
          [r2 * aX, r2 * aY, 0.0],
          [r2 * aX, r2 * aY, 0.0]
        )
        position.push(
          [0.0, 0.0, 0.0],
          [r2 * aX, r2 * aY, 0.0],
          [r2 * bX, r2 * bY, 0.0]
        )
        position.push(
          [0.0, 0.0, 0.0],
          [r2 * bX, r2 * bY, 0.0],
          [r2 * bX, r2 * bY, 0.0]
        )
        normal.push([0.0, 0.0, 1.0], [0.0, 0.0, 1.0], [0.0, 0.0, 1.0])
        normal.push([0.0, 0.0, 1.0], [0.0, 0.0, 1.0], [0.0, 0.0, 1.0])
        normal.push([0.0, 0.0, 1.0], [0.0, 0.0, 1.0], [0.0, 0.0, 1.0])

        uv.push([uA, r1], [uA, r2], [(uA + uB) / 2, r1])
        uv.push([(uA + uB) / 2, r1], [uA, r2], [uB, r2])
        uv.push([(uA + uB) / 2, r1], [uB, r2], [uB, r1])

        uv2.push([uA, r1], [uA, r2], [(uA + uB) / 2, r1])
        uv2.push([(uA + uB) / 2, r1], [uA, r2], [uB, r2])
        uv2.push([(uA + uB) / 2, r1], [uB, r2], [uB, r1])
      } else {
        position.push(
          [r1 * aX, r1 * aY, 0.0],
          [r2 * aX, r2 * aY, 0.0],
          [r2 * bX, r2 * bY, 0.0]
        )
        normal.push([0.0, 1.0, 0.0], [0.0, 1.0, 0.0], [0.0, 1.0, 0.0])
        uv.push([uA, r1], [uA, r2], [uB, r2])
        uv2.push([uA, r1], [uA, r2], [uB, r2])

        position.push(
          [r1 * aX, r1 * aY, 0.0],
          [r2 * bX, r2 * bY, 0.0],
          [r1 * bX, r1 * bY, 0.0]
        )
        normal.push([0.0, 1.0, 0.0], [0.0, 1.0, 0.0], [0.0, 1.0, 0.0])
        uv.push([uA, r1], [uB, r2], [uB, r1])
        uv2.push([uA, r1], [uB, r2], [uB, r1])
      }
    }
  }

  return { position, normal, uv, uv2 }
}

const createSphereAttributes = (isHalfSphere = false) => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()
  const uv = new Array<Vec2>()

  const latitudeBands = cylinderVSegments * 2
  const longitudeBands = cylinderUSegments

  // For half sphere, we only go from 0 to PI/2 (top half)
  const phiStart = 0
  const phiLength = isHalfSphere ? Math.PI / 2 : Math.PI

  const angle = (2 * Math.PI) / longitudeBands

  // Create vertices
  for (let latNumber = 0; latNumber <= latitudeBands; latNumber++) {
    const theta = (latNumber * phiLength) / latitudeBands + phiStart
    const sinTheta = Math.sin(theta)
    const cosTheta = Math.cos(theta)

    const longitudeStart = 0
    const longitudeEnd = longitudeBands

    for (
      let longNumber = longitudeStart;
      longNumber <= longitudeEnd;
      longNumber++
    ) {
      const phi = longNumber * angle
      const sinPhi = Math.sin(phi)
      const cosPhi = Math.cos(phi)

      const x = cosPhi * sinTheta
      const y = cosTheta
      const z = sinPhi * sinTheta

      const u = longNumber / longitudeBands
      const v = 1.0 - latNumber / latitudeBands

      if (latNumber < latitudeBands && longNumber < longitudeEnd) {
        const p1 = [x, y, z] as Vec3
        const p2 = [
          Math.cos(phi + angle) * sinTheta,
          cosTheta,
          Math.sin(phi + angle) * sinTheta,
        ] as Vec3
        const p3 = [
          cosPhi * Math.sin(theta + phiLength / latitudeBands),
          Math.cos(theta + phiLength / latitudeBands),
          sinPhi * Math.sin(theta + phiLength / latitudeBands),
        ] as Vec3

        position.push(p1, p2, p3)

        normal.push(p1, p2, p3)

        uv.push([u, v], [u + 1 / longitudeBands, v], [u, v - 1 / latitudeBands])

        const p4 = [
          Math.cos(phi + angle) * sinTheta,
          cosTheta,
          Math.sin(phi + angle) * sinTheta,
        ] as Vec3
        const p5 = [
          Math.cos(phi + angle) * Math.sin(theta + phiLength / latitudeBands),
          Math.cos(theta + phiLength / latitudeBands),
          Math.sin(phi + angle) * Math.sin(theta + phiLength / latitudeBands),
        ] as Vec3
        const p6 = [
          cosPhi * Math.sin(theta + phiLength / latitudeBands),
          Math.cos(theta + phiLength / latitudeBands),
          sinPhi * Math.sin(theta + phiLength / latitudeBands),
        ] as Vec3

        position.push(p4, p5, p6)

        normal.push(p4, p5, p6)

        uv.push(
          [u + 1 / longitudeBands, v],
          [u + 1 / longitudeBands, v - 1 / latitudeBands],
          [u, v - 1 / latitudeBands]
        )
      }
    }
  }

  return { position, normal, uv }
}

const createRadianSphereAttributes = (isHalfSphere = true) => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()
  const uv = new Array<Vec2>()
  const uv2 = new Array<Vec2>()
  const omegaRadianMultiplier = isHalfSphere ? 0.5 : 1.0

  for (let j = 0; j < radialSegments; j++) {
    for (let i = 0; i < angleSegments; i++) {
      const r1 = j / radialSegments
      const r2 = (j + 1) / radialSegments
      const uA = i / angleSegments
      const uB = (i + 1) / angleSegments
      const phiA = 2.0 * Math.PI * uA
      const phiB = 2.0 * Math.PI * uB

      const omegaA = r1 * Math.PI * omegaRadianMultiplier
      const omegaB = r2 * Math.PI * omegaRadianMultiplier

      const aX = Math.sin(omegaA) * -Math.cos(phiA) // r1, uA
      const aY = Math.cos(-omegaA)
      const aZ = Math.sin(omegaA) * Math.sin(phiA)

      const bX = Math.sin(omegaA) * -Math.cos(phiB) // r1, uB
      const bY = Math.cos(-omegaA)
      const bZ = Math.sin(omegaA) * Math.sin(phiB)

      const cX = Math.sin(omegaB) * -Math.cos(phiA) // r2, uA
      const cY = Math.cos(-omegaB)
      const cZ = Math.sin(omegaB) * Math.sin(phiA)

      const dX = Math.sin(omegaB) * -Math.cos(phiB) // r2, uB
      const dY = Math.cos(-omegaB)
      const dZ = Math.sin(omegaB) * Math.sin(phiB)
      if (j === 0) {
        position.push([0.0, 1.0, 0.0], [cX, cY, cZ], [cX, cY, cZ])
        position.push([0.0, 1.0, 0.0], [cX, cY, cZ], [dX, dY, dZ])
        position.push([0.0, 1.0, 0.0], [dX, dY, dZ], [dX, dY, dZ])

        normal.push([0.0, 1.0, 0.0], [cX, cY, cZ], [cX, cY, cZ])
        normal.push([0.0, 1.0, 0.0], [cX, cY, cZ], [dX, dY, dZ])
        normal.push([0.0, 1.0, 0.0], [dX, dY, dZ], [dX, dY, dZ])

        uv.push([uA, r1], [(uA + uB) / 2, r1], [uA, r2])
        uv.push([(uA + uB) / 2, r1], [uB, r2], [uA, r2])
        uv.push([(uA + uB) / 2, r1], [uB, r1], [uB, r2])

        uv2.push([uA, r1], [(uA + uB) / 2, r1], [uA, r2])
        uv2.push([(uA + uB) / 2, r1], [uB, r2], [uA, r2])
        uv2.push([(uA + uB) / 2, r1], [uB, r1], [uB, r2])
      } else {
        position.push([aX, aY, aZ], [cX, cY, cZ], [bX, bY, bZ])

        normal.push([aX, aY, aZ], [cX, cY, cZ], [bX, bY, bZ])

        uv.push([uA, r1], [uA, r2], [uB, r1])
        uv2.push([uA, r1], [uA, r2], [uB, r1])

        position.push([bX, bY, bZ], [cX, cY, cZ], [dX, dY, dZ])

        normal.push([bX, bY, bZ], [cX, cY, cZ], [dX, dY, dZ])

        uv.push([uB, r1], [uA, r2], [uB, r2])
        uv2.push([uB, r1], [uA, r2], [uB, r2])
      }
    }
  }

  return { position, normal, uv, uv2 }
}

/**
 * Creates a quarter torus geometry with the specified dimensions
 * @param majorRadius Distance from the center of the torus to the center of the tube
 * @param minorRadius Radius of the tube
 * @returns BufferGeometry for the quarter torus
 */
export const createQuarterTorusGeometry = (
  majorRadius: number,
  minorRadius: number,
  angle: number = 90
) => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()
  const uv = new Array<Vec2>()

  const tubeSegments = bendTubeSegments
  const segments = cylinderUSegments

  const startAngle = 0
  const endAngle = (angle * Math.PI) / 180 // Degree to radian

  const calculateTorusPoint = (phi: number, theta: number): Vec3 => {
    const cosPhi = Math.cos(phi)
    const sinPhi = Math.sin(phi)
    const cosTheta = Math.cos(theta)
    const sinTheta = Math.sin(theta)

    const x = minorRadius * sinTheta
    const y = (majorRadius + minorRadius * cosTheta) * cosPhi
    const z = (majorRadius + minorRadius * cosTheta) * sinPhi

    // Apply same coordinate transformation as normals: newX = oldY, newY = oldZ, newZ = oldX
    return [y, z, x]
  }

  for (let i = 0; i < segments; i++) {
    // Phi goes around the bend of the elbow
    const phi1 = startAngle + (i / segments) * (endAngle - startAngle)
    const phi2 = startAngle + ((i + 1) / segments) * (endAngle - startAngle)

    for (let j = 0; j < tubeSegments; j++) {
      const theta1 = (j / tubeSegments) * 2 * Math.PI
      const theta2 = ((j + 1) / tubeSegments) * 2 * Math.PI

      const p1 = calculateTorusPoint(phi1, theta1)
      const p2 = calculateTorusPoint(phi2, theta1)
      const p3 = calculateTorusPoint(phi2, theta2)
      const p4 = calculateTorusPoint(phi1, theta2)

      position.push(p1, p2, p3)

      position.push(p1, p3, p4)

      const calculateNormal = (phi: number, theta: number): Vec3 => {
        const cosPhi = Math.cos(phi)
        const sinPhi = Math.sin(phi)
        const cosTheta = Math.cos(theta)
        const sinTheta = Math.sin(theta)

        // Original normal components
        const Nz = sinPhi * cosTheta
        const Nx = sinTheta
        const Ny = cosPhi * cosTheta

        const length = Math.sqrt(Nz * Nz + Nx * Nx + Ny * Ny)

        // Apply same coordinate transformation: newX = oldY, newY = oldZ, newZ = oldX
        return [Nx / length, Ny / length, Nz / length]
      }

      const n1 = calculateNormal(phi1, theta1)
      const n2 = calculateNormal(phi2, theta1)
      const n3 = calculateNormal(phi2, theta2)
      const n4 = calculateNormal(phi1, theta2)

      normal.push(n1, n2, n3)

      normal.push(n1, n3, n4)

      const u1 = i / segments
      const u2 = (i + 1) / segments
      const v1 = j / tubeSegments
      const v2 = (j + 1) / tubeSegments

      // Invert v1 and v2 to make the circumferential direction go clockwise
      uv.push([1 - v1, u1], [1 - v1, u2], [1 - v2, u2])
      uv.push([1 - v1, u1], [1 - v2, u2], [1 - v2, u1])
    }
  }

  return createBufferGeometry({ position, normal, uv, uv2: uv })
}

/**
 * Creates a T-Joint Geometry with the specified dimensions
 * @param chordLength The length of the chord of the joint
 * @param chordRadius Radius of the chord
 * @param braceLength The length of the brace of the joint
 * @param braceRadius Radius of the brace, brace radius assumed to be <= chord radius
 * @param maxX Maximum value for the x coordinate - needed to fit the component in the 3D Bounds
 * @param maxY Maximum value for the y coordinate - needed to fit the component in the 3D Bounds
 */
export const createTJointPlaneGeometry = (
  chordLength: number,
  chordRadius: number,
  braceLength: number,
  braceRadius: number,
  maxX: number, // Needed to bound the object within the component bounds
  maxY: number
) => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()

  const chordMinX = 0
  const chordMaxX = chordLength
  const chordMinY = -chordRadius * 2 * Math.PI
  const chordMaxY = 0

  const braceMinX = chordLength / 2 - braceRadius * Math.PI
  const braceMaxX = chordLength / 2 + braceRadius * Math.PI
  const braceMinY = 0
  const braceMaxY = braceLength

  const overallMinX = Math.min(chordMinX, braceMinX)
  const overallMaxX = Math.max(chordMaxX, braceMaxX)
  const overallMinY = Math.min(chordMinY, braceMinY)
  const overallMaxY = Math.max(chordMaxY, braceMaxY)

  const actualWidth = overallMaxX - overallMinX
  const actualHeight = overallMaxY - overallMinY

  const bufferX = maxX * 0.1
  const bufferY = maxY * 0.1
  const availableWidth = maxX - 2 * bufferX
  const availableHeight = maxY - 2 * bufferY

  const scaleX = availableWidth / actualWidth
  const scaleY = availableHeight / actualHeight
  const scale = Math.min(scaleX, scaleY)

  const scaledWidth = actualWidth * scale
  const scaledHeight = actualHeight * scale
  const offsetX = (maxX - scaledWidth) / 2 - overallMinX * scale
  const offsetY = (maxY - scaledHeight) / 2 - overallMinY * scale

  for (let x = 0; x < planeSegments; x++) {
    for (let y = 0; y < planeSegments; y++) {
      // Chord grows vertically
      const chordX1 = (x / planeSegments) * chordLength
      const chordY1 = ((-1 * y) / planeSegments) * chordRadius * 2 * Math.PI
      const chordX2 = ((x + 1) / planeSegments) * chordLength
      const chordY2 =
        ((-1 * (y + 1)) / planeSegments) * chordRadius * 2 * Math.PI

      const scaledChordX1 = chordX1 * scale + offsetX
      const scaledChordY1 = chordY1 * scale + offsetY
      const scaledChordX2 = chordX2 * scale + offsetX
      const scaledChordY2 = chordY2 * scale + offsetY

      position.push(
        [scaledChordX1, scaledChordY1, 1.0],
        [scaledChordX2, scaledChordY1, 1.0],
        [scaledChordX2, scaledChordY2, 1.0]
      )
      position.push(
        [scaledChordX2, scaledChordY2, 1.0],
        [scaledChordX1, scaledChordY2, 1.0],
        [scaledChordX1, scaledChordY1, 1.0]
      )
      normal.push([0.0, 0.0, 1.0], [0.0, 0.0, 1.0], [0.0, 0.0, 1.0])
      normal.push([0.0, 0.0, 1.0], [0.0, 0.0, 1.0], [0.0, 0.0, 1.0])

      // Brace grows horizontally
      const braceX1 =
        (x / planeSegments) * braceRadius * 2 * Math.PI +
        chordLength / 2 -
        braceRadius * Math.PI
      const braceY1 = (y / planeSegments) * braceLength
      const braceX2 =
        ((x + 1) / planeSegments) * braceRadius * 2 * Math.PI +
        chordLength / 2 -
        braceRadius * Math.PI
      const braceY2 = ((y + 1) / planeSegments) * braceLength

      const scaledBraceX1 = braceX1 * scale + offsetX
      const scaledBraceY1 = braceY1 * scale + offsetY
      const scaledBraceX2 = braceX2 * scale + offsetX
      const scaledBraceY2 = braceY2 * scale + offsetY

      position.push(
        [scaledBraceX1, scaledBraceY1, 1.0],
        [scaledBraceX2, scaledBraceY1, 1.0],
        [scaledBraceX2, scaledBraceY2, 1.0]
      )
      position.push(
        [scaledBraceX2, scaledBraceY2, 1.0],
        [scaledBraceX1, scaledBraceY2, 1.0],
        [scaledBraceX1, scaledBraceY1, 1.0]
      )
      normal.push([0.0, 0.0, 1.0], [0.0, 0.0, 1.0], [0.0, 0.0, 1.0])
      normal.push([0.0, 0.0, 1.0], [0.0, 0.0, 1.0], [0.0, 0.0, 1.0])
    }
  }

  return createBufferGeometry({ position, normal })
}

/**
 * Creates a T-Joint Geometry with the specified dimensions
 * @param chordLength The length of the chord of the joint
 * @param chordRadius Radius of the chord
 * @param braceLength The length of the brace of the joint
 * @param braceRadius Radius of the brace, brace radius assumed to be <= chord radius
 */
export const createTJointGeometry = (
  chordLength: number,
  chordRadius: number,
  braceLength: number,
  braceRadius: number
) => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()

  const segments = 256
  const iterations = 256

  // At most the brace radius can be the chord radius for now
  braceRadius = Math.min(braceRadius, chordRadius)

  // Set a limit for brace height and adjust brace length so that it can be fully drawn
  const braceLimit = braceLength + chordRadius
  braceLength += 2 * chordRadius

  const getCircleCoords = (theta: number, radius: number) => {
    const cosTheta = Math.cos(theta)
    const sinTheta = Math.sin(theta)

    const horizontal = sinTheta * radius
    const vertical = cosTheta * radius
    return {
      horizontal, // x (brace), y (chord)
      vertical, // y (brace), z (chord)
    }
  }

  const clamp = (v: number) => Math.max(0, v)

  // Remove chord triangles that are "inside" the brace cylinder (x^2 + y^2 <= braceRadius^2)
  // AND lie on/above the chord surface where the brace emerges (z >= 0) since the chord bottom half has z < 0
  const shouldKeepChordTri = (a: Vec3, b: Vec3, c: Vec3) => {
    const cx = (a[0] + b[0] + c[0]) / 3
    const cy = (a[1] + b[1] + c[1]) / 3
    const cz = (a[2] + b[2] + c[2]) / 3

    const insideBrace = cx * cx + cy * cy <= braceRadius * braceRadius

    if (insideBrace && cz > 0) return false
    return true
  }

  // Remove brace triangles that are outside the brace limit height
  const shouldKeepBraceTri = (a: Vec3, b: Vec3, c: Vec3) => {
    const cz = (a[2] + b[2] + c[2]) / 3
    if (cz > braceLimit) return false
    return true
  }

  for (let j = 0; j < iterations; j++) {
    const t0 = j / iterations
    const t1 = (j + 1) / iterations

    for (let i = 0; i < segments; i++) {
      const theta1 = (i / segments) * 2 * Math.PI
      const theta2 = ((i + 1) / segments) * 2 * Math.PI

      const chordMeta1 = getCircleCoords(theta1, chordRadius)
      const chordMeta2 = getCircleCoords(theta2, chordRadius)

      const getChordPoint = (
        t: number,
        chordMeta: ReturnType<typeof getCircleCoords>,
        normalOnly?: boolean
      ): Vec3 => [
        normalOnly ? 0 : chordLength * t - chordLength / 2,
        chordMeta.horizontal,
        chordMeta.vertical,
      ]

      let points: Array<Vec3> = [
        getChordPoint(t0, chordMeta1),
        getChordPoint(t1, chordMeta1),
        getChordPoint(t0, chordMeta2),
        getChordPoint(t1, chordMeta2),
      ]
      if (shouldKeepChordTri(points[0], points[1], points[2])) {
        position.push(points[0], points[1], points[2])
        normal.push(
          getChordPoint(t0, chordMeta1, true),
          getChordPoint(t1, chordMeta1, true),
          getChordPoint(t0, chordMeta2, true)
        )
      }
      if (shouldKeepChordTri(points[1], points[2], points[3])) {
        position.push(points[1], points[2], points[3])
        normal.push(
          getChordPoint(t1, chordMeta1, true),
          getChordPoint(t1, chordMeta2, true),
          getChordPoint(t1, chordMeta2, true)
        )
      }

      const braceMeta1 = getCircleCoords(theta1, braceRadius)
      const braceMeta2 = getCircleCoords(theta2, braceRadius)

      // chord: y^2 + z^2 = Rc^2  =>  z0(y) = +sqrt(Rc^2 - y^2)
      // We start each vertex at z0(y) instead of a flat plane.
      const z1 = Math.sqrt(
        clamp(
          chordRadius * chordRadius - braceMeta1.vertical * braceMeta1.vertical
        )
      )
      const z2 = Math.sqrt(
        clamp(
          chordRadius * chordRadius - braceMeta2.vertical * braceMeta2.vertical
        )
      )

      // Build two rings at t0 and t1, each lifted from the local base z0 by the same axial delta
      const getBracePoint = (
        t: number,
        z: number,
        braceMeta: ReturnType<typeof getCircleCoords>,
        normalOnly?: boolean
      ): Vec3 => [
        braceMeta.horizontal,
        braceMeta.vertical,
        normalOnly ? 0 : z + braceLength * t,
      ]

      points = [
        getBracePoint(t0, z1, braceMeta1),
        getBracePoint(t1, z1, braceMeta1),
        getBracePoint(t0, z2, braceMeta2),
        getBracePoint(t1, z2, braceMeta2),
      ]

      if (shouldKeepBraceTri(points[0], points[1], points[2])) {
        position.push(points[0], points[1], points[2])
        normal.push(
          getBracePoint(t0, z1, braceMeta1, true),
          getBracePoint(t1, z1, braceMeta1, true),
          getBracePoint(t0, z2, braceMeta2, true)
        )
      }
      if (shouldKeepBraceTri(points[1], points[2], points[3])) {
        position.push(points[1], points[2], points[3])
        normal.push(
          getBracePoint(t1, z1, braceMeta1, true),
          getBracePoint(t0, z2, braceMeta2, true),
          getBracePoint(t1, z2, braceMeta2, true)
        )
      }
    }
  }

  return createBufferGeometry({ position, normal })
}

/**
 * Creates an Eccentric Reducer 2D Geometry with the specified dimensions
 * @param length The length of the pipe
 * @param inletRadius Radius of the inlet of the pipe (usually this is the larger end)
 * @param outletRadius Radius of the outlet of the pipe (usually this is the smaller end)
 */
export const createEccentricReducerPlaneGeometry = (
  length: number,
  inletRadius: number,
  outletRadius: number
) => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()
  const uv = new Array<Vec2>()

  const segments = bendTubeSegments
  const normalVec: Vec3 = [0.0, 0.0, 1.0]

  // 2 * pi * inletRadius / 2
  const center = Math.PI * inletRadius

  const getCircumference = (xSegment: number) => {
    const radius =
      -1 * Math.abs(outletRadius - inletRadius) * xSegment +
      Math.max(inletRadius, outletRadius)
    return 2 * Math.PI * radius
  }

  const getPoint = (c: number, xSegment: number, ySegment: number): Vec3 => {
    const y = xSegment * length
    const x = center + c * (0.5 - ySegment)
    return [x, y, 0]
  }

  // Used to normalze uvs
  const maxX = length
  const maxY = 2 * Math.PI * inletRadius

  for (let i = 0; i < segments; i++) {
    const t0 = i / segments
    const t1 = (i + 1) / segments
    const c0 = getCircumference(t0)
    const c1 = getCircumference(t1)

    for (let j = 0; j < segments; j++) {
      const p1 = getPoint(c0, t0, j / segments)
      const p2 = getPoint(c1, t1, j / segments)
      const p3 = getPoint(c0, t0, (j + 1) / segments)
      const p4 = getPoint(c1, t1, (j + 1) / segments)

      position.push(p1, p2, p3)
      uv.push(
        [p1[1] / maxX, p1[0] / maxY],
        [p2[1] / maxX, p2[0] / maxY],
        [p3[1] / maxX, p3[0] / maxY]
      )
      normal.push(normalVec, normalVec, normalVec)

      position.push(p2, p3, p4)
      uv.push(
        [p2[1] / maxX, p2[0] / maxY],
        [p3[1] / maxX, p3[0] / maxY],
        [p4[1] / maxX, p4[0] / maxY]
      )
      normal.push(normalVec, normalVec, normalVec)
    }
  }

  return createBufferGeometry({ position, normal, uv })
}

/**
 * Creates an Eccentric Reducer Geometry with the specified dimensions
 * @param length The length of the pipe
 * @param inletRadius Radius of the inlet of the pipe (usually this is the larger end)
 * @param outletRadius Radius of the outlet of the pipe (usually this is the smaller end)
 */
export const createEccentricReducerGeometry = (
  length: number,
  inletRadius: number,
  outletRadius: number
) => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()
  const uv = new Array<Vec2>()

  const pipeSegments = bendTubeSegments
  const segments = cylinderUSegments
  const seamAngle = Math.PI / 2

  // The radius can be determined by using the line equation to interpolate between the inlet and outlet
  const getRadius = (segment: number) => {
    return (
      -1 * Math.abs(outletRadius - inletRadius) * segment +
      Math.max(inletRadius, outletRadius)
    )
  }

  const getPoint = (theta: number, segment: number): Vec3 => {
    const radius = getRadius(segment)
    const sinTheta = Math.sin(theta)
    const cosTheta = Math.cos(theta)

    const x = segment * length
    const y = radius * cosTheta
    const z = radius * sinTheta + radius // Readjust so that center of circle is radius above 0

    return [x, y, z]
  }

  const getNormal = (theta: number, segment: number): Vec3 => {
    const radius = getRadius(segment)
    const radiiDifference = inletRadius - outletRadius
    const sinTheta = Math.sin(theta)
    const cosTheta = Math.cos(theta)

    // Derived from the cross product between surface tangent components
    const x = radiiDifference * radius * (sinTheta + 1)
    const y = length * radius * cosTheta
    const z = -1 * length * radius * sinTheta

    return [x, y, z]
  }

  const getUV = (theta: number, xSegment: number): Vec2 => {
    const vSegment = (theta - seamAngle) / (2 * Math.PI)
    const u = xSegment
    const v = vSegment
    return [u, v]
  }

  for (let i = 0; i < pipeSegments; i++) {
    const t0 = i / pipeSegments
    const t1 = (i + 1) / pipeSegments

    for (let j = 0; j < segments; j++) {
      // Seam where we unwrap the object for 2D is at theta = PI / 2
      const theta1 = (j / segments) * 2 * Math.PI + seamAngle
      const theta2 = ((j + 1) / segments) * 2 * Math.PI + seamAngle

      const p1 = getPoint(theta1, t0)
      const p2 = getPoint(theta2, t0)
      const p3 = getPoint(theta1, t1)
      const p4 = getPoint(theta2, t1)

      const p1N = getNormal(theta1, t0)
      const p2N = getNormal(theta2, t0)
      const p3N = getNormal(theta1, t1)
      const p4N = getNormal(theta2, t1)

      const p1UV = getUV(theta1, t0)
      const p2UV = getUV(theta2, t0)
      const p3UV = getUV(theta1, t1)
      const p4UV = getUV(theta2, t1)

      position.push(p1, p2, p3)
      uv.push(p1UV, p2UV, p3UV)
      normal.push(p1N, p2N, p3N)

      position.push(p2, p3, p4)
      uv.push(p2UV, p3UV, p4UV)
      normal.push(p2N, p3N, p4N)
    }
  }

  return createBufferGeometry({ position, normal, uv })
}

export const get3DConeGeometry = (
  innerRadius: number,
  outerRadius: number,
  coneHeight: number
) => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()
  const uv = new Array<Vec2>()
  const uv2 = new Array<Vec2>()

  const scaledOuterRadius = outerRadius / Math.max(innerRadius, outerRadius)
  const scaledInnerRadius = innerRadius / Math.max(innerRadius, outerRadius)

  for (let i = 0; i < angleSegments; i++) {
    const uA = i / angleSegments
    const uB = (i + 1) / angleSegments
    const angleA = 2.0 * Math.PI * uA
    const angleB = 2.0 * Math.PI * uB

    const aX = -Math.cos(angleA)
    const aZ = Math.sin(angleA)
    const bX = -Math.cos(angleB)
    const bZ = Math.sin(angleB)

    const ax1 = outerRadius * aX
    const ay1 = 0.0
    const az1 = outerRadius * aZ

    const bx1 = outerRadius * bX
    const by1 = 0.0
    const bz1 = outerRadius * bZ

    const ax2 = innerRadius * aX
    const ay2 = coneHeight
    const az2 = innerRadius * aZ

    const bx2 = innerRadius * bX
    const by2 = coneHeight
    const bz2 = innerRadius * bZ

    // cross product, derive normal
    const ux = bx1 - ax1
    const uy = by1 - ay1
    const uz = bz1 - az1

    const vx = ax2 - ax1
    const vy = ay2 - ay1
    const vz = az2 - az1

    const cpx = uy * vz - uz * vy
    const cpy = -(ux * vz - vx * uz)
    const cpz = ux * vy - vx * uz

    const normalizationFactor = Math.sqrt(cpx * cpx + cpy * cpy + cpz * cpz)

    const nx = cpx / normalizationFactor
    const ny = cpy / normalizationFactor
    const nz = cpz / normalizationFactor

    position.push([ax1, ay1, az1], [bx1, by1, bz1], [ax2, ay2, az2])
    normal.push([nx, ny, nz], [nx, ny, nz], [nx, ny, nz])

    uv.push(
      [uA, scaledOuterRadius],
      [uB, scaledOuterRadius],
      [uA, scaledInnerRadius]
    )
    uv2.push(
      [uA, scaledOuterRadius],
      [uB, scaledOuterRadius],
      [uA, scaledInnerRadius]
    )

    position.push([ax2, ay2, az2], [bx1, by1, bz1], [bx2, by2, bz2])
    normal.push([nx, ny, nz], [nx, ny, nz], [nx, ny, nz])

    uv.push(
      [uA, scaledInnerRadius],
      [uB, scaledOuterRadius],
      [uB, scaledInnerRadius]
    )
    uv2.push(
      [uA, scaledInnerRadius],
      [uB, scaledOuterRadius],
      [uB, scaledInnerRadius]
    )
  }
  return createBufferGeometry({ position, normal, uv, uv2: uv })
}

//------------------------------------------------------------------------------

export const getDeckGeometry = (
  length: number,
  startWidth: number,
  endWidth: number
) => {
  const maxWidth = Math.max(startWidth, endWidth)
  const t1x1 = maxWidth / 2 - startWidth / 2
  const t1x2 = maxWidth / 2 + endWidth / 2
  const t1x3 = maxWidth / 2 - endWidth / 2

  const t1y1 = 0
  const t1y2 = length
  const t1y3 = length

  const t2x1 = maxWidth / 2 - startWidth / 2
  const t2x2 = maxWidth / 2 + startWidth / 2
  const t2x3 = maxWidth / 2 + endWidth / 2

  const t2y1 = 0
  const t2y2 = 0
  const t2y3 = length

  const position: Array<Vec3> = [
    [t1x1, t1y1, 0],
    [t1x2, t1y2, 0],
    [t1x3, t1y3, 0],
    [t2x1, t2y1, 0],
    [t2x2, t2y2, 0],
    [t2x3, t2y3, 0],
  ]

  const normal: Array<Vec3> = [
    [0, 0, 1.0],
    [0, 0, 1.0],
    [0, 0, 1.0],
    [0, 0, 1.0],
    [0, 0, 1.0],
    [0, 0, 1.0],
  ]

  const uv: Array<Vec2> = position.map(([x, y]) => [x / maxWidth, y / length])

  return createBufferGeometry({ position, normal, uv, uv2: uv })
}

export const createHalfShellAttributes = (
  height: number,
  outerRadius: number,
  thickness: number
) => {
  const position = new Array<Vec3>()
  const normal = new Array<Vec3>()

  const innerRadius = outerRadius - thickness

  for (let i = cylinderUSegments / 2; i < cylinderUSegments; i++) {
    const uA = i / cylinderUSegments
    const uB = (i + 1) / cylinderUSegments
    const y0 = 0
    const y1 = height

    const angleA = 2.0 * Math.PI * uA
    const angleB = 2.0 * Math.PI * uB

    const aX = -Math.cos(angleA)
    const aZ = Math.sin(angleA)
    const bX = -Math.cos(angleB)
    const bZ = Math.sin(angleB)

    const outerXA = aX * outerRadius
    const outerZA = aZ * outerRadius
    const outerXB = bX * outerRadius
    const outerZB = bZ * outerRadius

    const innerXA = aX * innerRadius
    const innerZA = aZ * innerRadius
    const innerXB = bX * innerRadius
    const innerZB = bZ * innerRadius

    if (y1 === height) {
      position.push(
        [outerXB, height, outerZB],
        [innerXA, height, innerZA],
        [outerXA, height, outerZA]
      )
      position.push(
        [innerXB, height, innerZB],
        [innerXA, height, innerZA],
        [outerXB, height, outerZB]
      )
      normal.push([0, 1, 0], [0, 1, 0], [0, 1, 0])
      normal.push([0, 1, 0], [0, 1, 0], [0, 1, 0])
    }

    if (y0 === 0) {
      position.push(
        [outerXA, 0, outerZA],
        [innerXA, 0, innerZA],
        [outerXB, 0, outerZB]
      )
      position.push(
        [outerXB, 0, outerZB],
        [innerXA, 0, innerZA],
        [innerXB, 0, innerZB]
      )
      normal.push([0, -1, 0], [0, -1, 0], [0, -1, 0])
      normal.push([0, -1, 0], [0, -1, 0], [0, -1, 0])
    }
    if (angleA === Math.PI) {
      position.push(
        [outerXA, height, outerZA],
        [innerXA, 0, innerZA],
        [outerXA, 0, outerZA]
      )
      position.push(
        [outerXA, height, outerZA],
        [innerXA, height, innerZA],
        [innerXA, 0, innerZA]
      )
      normal.push([0, 0, 1], [0, 0, 1], [0, 0, 1])
      normal.push([0, 0, 1], [0, 0, 1], [0, 0, 1])
    }
    if (angleB === 2 * Math.PI) {
      position.push(
        [outerXB, 0, outerZB],
        [innerXB, 0, innerZB],
        [outerXB, height, outerZB]
      )
      position.push(
        [innerXB, 0, innerZB],
        [innerXB, height, innerZB],
        [outerXB, height, outerZB]
      )
      normal.push([0, 0, 1], [0, 0, 1], [0, 0, 1])
      normal.push([0, 0, 1], [0, 0, 1], [0, 0, 1])
    }

    // Outer shell
    position.push(
      [outerXA, y0, outerZA],
      [outerXB, y0, outerZB],
      [outerXB, y1, outerZB]
    )
    position.push(
      [outerXB, y1, outerZB],
      [outerXA, y1, outerZA],
      [outerXA, y0, outerZA]
    )
    normal.push(
      [outerXA, 0, outerZA],
      [outerXB, 0, outerZB],
      [outerXB, 0, outerZB]
    )
    normal.push(
      [outerXB, 0, outerZB],
      [outerXA, 0, outerZA],
      [outerXA, 0, outerZA]
    )

    // Inner Shell
    position.push(
      [innerXB, y1, innerZB],
      [innerXB, y0, innerZB],
      [innerXA, y0, innerZA]
    )
    position.push(
      [innerXA, y0, innerZA],
      [innerXA, y1, innerZA],
      [innerXB, y1, innerZB]
    )
    normal.push(
      [-innerXA, 0, -innerZA],
      [-innerXB, 0, -innerZB],
      [-innerXB, 0, -innerZB]
    )
    normal.push(
      [-innerXB, 0, -innerZB],
      [-innerXA, 0, -innerZA],
      [-innerXA, 0, -innerZA]
    )
  }
  return createBufferGeometry({ position, normal })
}

export const planeGeometry = (() => {
  const { position, normal, uv } = createPlaneAttributes()
  return createBufferGeometry({ position, normal, uv, uv2: uv })
})()

export const xWrappedShellGeometry = (() => {
  const { position, normal, uv } = createShellAttributes()
  return createBufferGeometry({ position, normal, uv, uv2: uv })
})()

export const yWrappedShellGeometry = (() => {
  const { position, normal, uv } = createShellAttributes()
  // Reverse all these as the normals flip directions
  const newPositions = position.map<Vec3>(([x, y, z]) => [y, x, z]).reverse()
  const newNormals = normal.map<Vec3>(([x, y, z]) => [y, x, z]).reverse()
  const newUV2s = uv.reverse() // reverse happens in place
  const newUVs = uv.map<Vec2>(([u, v]) => [v, u])
  return createBufferGeometry({
    position: newPositions,
    normal: newNormals,
    uv: newUVs,
    uv2: newUV2s,
  })
})()

export const cartesianCircleGeometry = (() => {
  const { position, normal, uv, uv2 } = createCircleAttributes()
  return createBufferGeometry({ position, normal, uv, uv2 })
})()

export const radialConeGeometry = (() => {
  const { position, normal, uv, uv2 } = createConeAttributes()
  return createBufferGeometry({ position, normal, uv, uv2 })
})()

export const sphereGeometry = (() => {
  const { position, normal, uv } = createSphereAttributes(true)
  return createBufferGeometry({ position, normal, uv, uv2: uv })
})()

export const radianSphereGeometry = (() => {
  const { position, normal, uv, uv2 } = createRadianSphereAttributes(true)
  return createBufferGeometry({ position, normal, uv, uv2 })
})()
